import { createContext, useContext, useState, ReactNode, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface ImpersonatedCreator {
  id: string;
  name: string;
  user_id: string;
}

interface ImpersonationContextType {
  impersonatedCreator: ImpersonatedCreator | null;
  startImpersonation: (creator: ImpersonatedCreator) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  isImpersonating: boolean;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error("useImpersonation must be used within an ImpersonationProvider");
  }
  return context;
};

/**
 * Admin impersonation for viewing creator dashboards.
 * 
 * SECURITY NOTE: This is a UI-only impersonation feature. The actual
 * data access is still protected by RLS policies that verify auth.uid().
 * Admins can only view data they already have database access to via
 * their admin role. This feature is for convenience in viewing the
 * creator experience without switching accounts.
 * 
 * All impersonation sessions are logged to impersonation_logs table
 * with start time, end time, admin user ID, and creator details.
 * 
 * Security measures in place:
 * - RLS policies on creators table: (auth.uid() = user_id)
 * - RLS policies on referrals table: (creator_id matches user_id via subquery)
 * - Impersonation logging for audit trail
 * - Admin authentication required before impersonation
 */
export const ImpersonationProvider = ({ children }: { children: ReactNode }) => {
  const [impersonatedCreator, setImpersonatedCreator] = useState<ImpersonatedCreator | null>(null);
  const currentLogIdRef = useRef<string | null>(null);

  const startImpersonation = async (creator: ImpersonatedCreator) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Log the impersonation start
      const { data, error } = await supabase
        .from("impersonation_logs")
        .insert({
          admin_user_id: user.id,
          creator_id: creator.id,
          creator_name: creator.name,
        })
        .select("id")
        .single();

      if (error) {
        console.error("Failed to log impersonation:", error);
      } else {
        currentLogIdRef.current = data.id;
      }

      setImpersonatedCreator(creator);
    } catch (err) {
      console.error("Error starting impersonation:", err);
      setImpersonatedCreator(creator);
    }
  };

  const stopImpersonation = async () => {
    try {
      // Update the log with end time
      if (currentLogIdRef.current) {
        await supabase
          .from("impersonation_logs")
          .update({ ended_at: new Date().toISOString() })
          .eq("id", currentLogIdRef.current);
        
        currentLogIdRef.current = null;
      }
    } catch (err) {
      console.error("Error logging impersonation end:", err);
    }
    setImpersonatedCreator(null);
  };

  return (
    <ImpersonationContext.Provider
      value={{
        impersonatedCreator,
        startImpersonation,
        stopImpersonation,
        isImpersonating: !!impersonatedCreator,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};
